import { useState, useCallback } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Settings, Monitor, FileCode } from "lucide-react"
import type { Config } from '@/api/types/config'
import { ModeSettings } from './workspace/mode-settings'
import { PromptSettings } from './workspace/prompt-settings'
import { SchemaEditor } from './workspace/schema'
import { FilterConfig } from './workspace/filter-config'

interface ConfigFormProps {
  config?: Config
  onSubmit: (data: Partial<Config>) => Promise<void>
  isLoading: boolean
}

// Standardized interface for all child components
interface ConfigComponentProps {
  config: Config | undefined
  onUpdate: (updates: Partial<Config>) => void
  isLoading: boolean
}

export function ConfigForm({ config, onSubmit, isLoading }: ConfigFormProps) {
  const [configUpdates, setConfigUpdates] = useState<Partial<Config>>({})
  const [isDirty, setIsDirty] = useState(false)

  const handleUpdate = useCallback((updates: Partial<Config>) => {
    setConfigUpdates(prev => ({ ...prev, ...updates }))
    setIsDirty(true)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isDirty) return
    
    await onSubmit(configUpdates)
    setConfigUpdates({})
    setIsDirty(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Workspace Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="general">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="general" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
                <Settings className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="schema" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
                <FileCode className="h-4 w-4" />
                Schema
              </TabsTrigger>
              <TabsTrigger value="prompts" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
                <Monitor className="h-4 w-4" />
                Prompts
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4">
              <ModeSettings 
                config={config} 
                onUpdate={handleUpdate} 
                isLoading={isLoading} 
              />
            </TabsContent>
            
            <TabsContent value="schema">
              <div className="space-y-4">
                <SchemaEditor 
                  config={config} 
                  onUpdate={handleUpdate} 
                  isLoading={isLoading} 
                />
                <FilterConfig 
                  config={config} 
                  onUpdate={handleUpdate} 
                  isLoading={isLoading} 
                />
              </div>
            </TabsContent>
            
            <TabsContent value="prompts">
              <PromptSettings 
                config={config} 
                onUpdate={handleUpdate} 
                isLoading={isLoading} 
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!isDirty || isLoading}
          className="bg-blue-700 hover:bg-blue-800 text-white font-medium"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}

// Export the standardized interface for use by child components
export type { ConfigComponentProps }